import { Button, Chip, DialogActions, DialogContent, DialogTitle, Divider, Modal, ModalDialog, Textarea, Typography } from "@mui/joy";
import React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

interface PackingListItem {
	packing_list_item_id: string;
	line_number: number;
	description: string;
	is_completed: boolean;
	completed_on_ticket_id?: string;
}

interface WorkOrderDialogProps {
	open: boolean;
	packingListId: string;
	clientName: string;
	estimateNumber: string;
	typeOfWork: string;
	initialNotes: string;
	onClose: () => void;
	onSaved: () => void;
}

/**
 * Dialog component for viewing work order details
 * Displays client name, estimate number, type of work, and line items with assignment status
 */
export default function WorkOrderDialog(props: WorkOrderDialogProps) {
	const [items, setItems] = React.useState<PackingListItem[]>([]);
	const [isLoading, setIsLoading] = React.useState(false);
	const [notes, setNotes] = React.useState("");
	const [isSaving, setIsSaving] = React.useState(false);

	React.useEffect(() => {
		if (props.open) setNotes(props.initialNotes ?? "");
	}, [props.open, props.initialNotes]);

	React.useEffect(() => {
		if (!props.open || !props.packingListId) return;

		setIsLoading(true);
		fetch("PackingList/GetPackingListItems", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ packingListId: props.packingListId }),
		})
			.then((x) => x.json())
			.then((x: PackingListItem[]) => {
				setItems(x);
				setIsLoading(false);
			});
	}, [props.open, props.packingListId]);

	const assignedItems = items.filter((i) => i.is_completed);
	const unassignedItems = items.filter((i) => !i.is_completed);

	const renderItem = (item: PackingListItem, assigned: boolean) => (
		<Typography
			key={item.packing_list_item_id}
			level="body-sm"
			sx={{ mb: 0.5, display: "flex", alignItems: "flex-start", gap: 0.75 }}>
			{assigned ? (
				<CheckCircleIcon sx={{ fontSize: 18, color: "success.500", mt: 0.1 }} />
			) : (
				<RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "neutral.400", mt: 0.1 }} />
			)}
			<span>
				{item.description}
				{assigned && item.completed_on_ticket_id && (
					<Typography component="span" level="body-xs" sx={{ ml: 1, color: "text.tertiary" }}>
						(linked to ticket)
					</Typography>
				)}
			</span>
		</Typography>
	);

	return (
		<Modal open={props.open}>
			<ModalDialog sx={{ width: 650, maxHeight: "80vh", overflowY: "auto" }}>
				<DialogTitle>Work Order Details</DialogTitle>
				<DialogContent>
					<Typography level="title-md" sx={{ mb: 0.5 }}>
						{props.clientName}
					</Typography>
					<Typography level="body-sm" sx={{ mb: 1.5 }}>
						Estimate #{props.estimateNumber}
					</Typography>

					<Divider sx={{ mb: 1.5 }} />

					<Typography level="title-sm" sx={{ mb: 0.5 }}>
						Type of Work
					</Typography>
					<Typography level="body-sm" sx={{ mb: 1.5 }}>
						{props.typeOfWork || "Ã¢â‚¬â€"}
					</Typography>

					<Divider sx={{ mb: 1.5 }} />

					{isLoading ? (
						<Typography level="body-sm">Loading...</Typography>
					) : items.length === 0 ? (
						<Typography level="body-sm">No items found.</Typography>
					) : (
						<>
							<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
								<Typography level="title-sm">Line Items</Typography>
								<Chip size="sm" variant="soft" color="success">
									{assignedItems.length} assigned
								</Chip>
								<Chip size="sm" variant="soft" color="warning">
									{unassignedItems.length} pending
								</Chip>
							</div>

							{assignedItems.length > 0 && (
								<>
									<Typography level="body-xs" sx={{ mb: 0.5, color: "success.600", fontWeight: 600 }}>
										Assigned to Ticket
									</Typography>
									{assignedItems.map((item) => renderItem(item, true))}
								</>
							)}

							{unassignedItems.length > 0 && (
								<>
									<Typography level="body-xs" sx={{ mt: assignedItems.length > 0 ? 1.5 : 0, mb: 0.5, color: "warning.700", fontWeight: 600 }}>
										Not Yet Assigned
									</Typography>
									{unassignedItems.map((item) => renderItem(item, false))}
								</>
							)}
						</>
					)}

					<Divider sx={{ mt: 1.5, mb: 1.5 }} />

					<Typography level="title-sm" sx={{ mb: 0.5 }}>
						Notes
					</Typography>
					<Textarea
						minRows={3}
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						placeholder="Enter notes..."
					/>
				</DialogContent>
				<DialogActions>
					<Button
						loading={isSaving}
						onClick={() => {
							setIsSaving(true);
							fetch("PackingList/SavePackingListNotes", {
								method: "POST",
								cache: "no-cache",
								headers: { "Content-Type": "application/json" },
								body: JSON.stringify({ packingListId: props.packingListId, notes }),
							}).then(() => {
								setIsSaving(false);
								props.onSaved();
							});
						}}
						variant="solid" color="success">
						Save Notes
					</Button>
					<Button onClick={props.onClose} color="neutral">
						Close
					</Button>
				</DialogActions>
			</ModalDialog>
		</Modal>
	);
}
